/* ProMechBC — Zoho Forms bridge.
 *
 * Two jobs:
 *   1. Email capture (footer newsletter, signup banner, Dawn newsletter section):
 *      every address is sent to the Zoho form set in
 *      Theme settings → ProMechBC → Zoho forms.
 *        mode "mirror" — Shopify still records the subscriber, Zoho gets a copy
 *                        (sent with sendBeacon so it survives the page reload).
 *        mode "zoho"   — the address only goes to Zoho; the form never reloads.
 *   2. Themed forms that post straight to Zoho (form[data-pmb-zoho="contact"]):
 *      the POST is retargeted into a hidden iframe so the shopper stays on the
 *      page, and the section's success panel is revealed on response.
 *
 * Config comes from the JSON in snippets/pmb-zoho-config.liquid.
 */
(function () {
  'use strict';

  var SINK = 'pmb-zoho-sink';
  var cfg = readConfig();
  var endpoint = cfg.endpoint ? submitUrl(cfg.endpoint) : '';
  var zohoOnly = cfg.mode === 'zoho';

  function readConfig() {
    var el = document.getElementById('pmb-zoho-config');
    if (!el) return {};
    try {
      return JSON.parse(el.textContent || '{}');
    } catch (e) {
      return {};
    }
  }

  /* Accepts a Zoho permalink with or without the /htmlRecords/submit suffix. */
  function submitUrl(url) {
    var base = String(url).trim().split('/htmlRecords')[0];
    return (base + '/htmlRecords/submit').replace('//htmlRecords', '/htmlRecords');
  }

  function post(payload) {
    var body = new URLSearchParams(payload).toString();
    try {
      if (navigator.sendBeacon) {
        var blob = new Blob([body], { type: 'application/x-www-form-urlencoded' });
        if (navigator.sendBeacon(endpoint, blob)) return;
      }
    } catch (e) {
      /* fall through to fetch */
    }
    try {
      fetch(endpoint, {
        method: 'POST',
        mode: 'no-cors',
        keepalive: true,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body
      });
    } catch (e) {
      /* nothing else we can do — the Shopify submit still goes through */
    }
  }

  function hiddenFrame() {
    var frame = document.getElementById(SINK);
    if (frame) return frame;
    frame = document.createElement('iframe');
    frame.id = SINK;
    frame.name = SINK;
    frame.title = 'Form response';
    frame.setAttribute('aria-hidden', 'true');
    frame.setAttribute('tabindex', '-1');
    frame.style.cssText = 'position:absolute;left:-9999px;width:0;height:0;border:0;';
    document.body.appendChild(frame);
    return frame;
  }

  function reveal(form, message) {
    /* Only a sibling panel belongs to this form — never one nested elsewhere. */
    var panel = form.parentNode && form.parentNode.querySelector(':scope > [data-pmb-zoho-success-panel]');
    if (panel) {
      panel.hidden = false;
    } else {
      panel = document.createElement('div');
      panel.className = 'pmb-zoho-success';
      panel.setAttribute('role', 'status');
      panel.textContent = message || cfg.successText || 'Thanks — you are on the list.';
      form.parentNode.insertBefore(panel, form);
    }
    form.hidden = true;
    form.style.display = 'none'; /* [hidden] loses to Dawn's display rules */
  }

  /* ---- Email capture ------------------------------------------------- */

  function emailField(form) {
    return form.querySelector('input[name="contact[email]"]');
  }

  function isSubscribeForm(form) {
    if (form.hasAttribute('data-pmb-zoho-skip')) return false;
    if (form.getAttribute('data-pmb-zoho') === 'subscribe') return !!emailField(form);
    if (cfg.auto === false) return false;
    var type = form.querySelector('input[name="form_type"]');
    return !!(type && type.value === 'customer' && emailField(form));
  }

  function initSubscribe(form) {
    form.addEventListener('submit', function (event) {
      if (event.defaultPrevented) return;
      var input = emailField(form);
      var email = input && input.value.trim();
      if (!email) return;

      var payload = {};
      payload[cfg.emailField || 'Email'] = email;
      if (cfg.sourceField) payload[cfg.sourceField] = window.location.href;
      if (cfg.referrerField) payload[cfg.referrerField] = document.referrer;
      post(payload);

      if (zohoOnly) {
        event.preventDefault();
        reveal(form);
      }
      /* mirror mode: let Shopify handle the visible submit as usual */
    });
  }

  /* ---- Themed forms posting straight to Zoho -------------------------- */

  /* Conditional groups: [data-pmb-when="FieldName::Value"] follows a radio.
     Hidden branches are disabled so Zoho never sees a half-filled branch. */
  function initBranches(form) {
    var groups = form.querySelectorAll('[data-pmb-when]');
    if (!groups.length) return;

    function sync() {
      groups.forEach(function (group) {
        var parts = group.getAttribute('data-pmb-when').split('::');
        var picked = form.querySelector('input[name="' + parts[0] + '"]:checked');
        var show = !!picked && picked.value === parts[1];
        group.hidden = !show;
        group.querySelectorAll('input, select, textarea').forEach(function (el) {
          el.disabled = !show;
          if (!show) el.checked = false;
        });
        if (!show) group.classList.remove('is-invalid', 'is-valid');
      });
    }

    form.addEventListener('change', function (e) {
      if (e.target.type === 'radio') sync();
    });
    sync();
  }

  function initDirect(form) {
    var frame = hiddenFrame();
    form.setAttribute('target', SINK);
    initBranches(form);
    form.addEventListener('submit', function (event) {
      if (event.defaultPrevented) return;
      var settled = false;
      var finish = function () {
        if (settled) return;
        settled = true;
        reveal(form);
      };
      frame.addEventListener('load', finish, { once: true });
      window.setTimeout(finish, 8000);
    });
  }

  /* ---- Embedded Zoho form: grow the iframe to fit its content ---------- */

  /* Zoho's hosted form posts "<permalink>|<height>" (a third part means it
     changed step and wants the frame scrolled into view) to the parent window
     when the src carries ?zf_rszfm=1. Without this the embed needs a guessed
     fixed height and shows either a scrollbar or dead space. */
  function initEmbedResize() {
    /* Reveal on load — kept here rather than an inline onload so a strict
       content-security-policy cannot strip it. */
    function watchFrames(root) {
      (root || document).querySelectorAll('.pmb-zoho__frame').forEach(function (frame) {
        if (frame.dataset.pmbWatch) return;
        frame.dataset.pmbWatch = '1';
        var show = function () { frame.closest('.pmb-zoho')?.classList.add('is-loaded'); };
        frame.addEventListener('load', show);
        /* Never leave the shopper looking at a spinner if load never fires. */
        window.setTimeout(show, 8000);
      });
    }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { watchFrames(document); });
    } else {
      watchFrames(document);
    }
    document.addEventListener('shopify:section:load', function (e) { watchFrames(e.target); });

    window.addEventListener('message', function (event) {
      if (String(event.origin).indexOf('zohopublic.com') === -1) return;
      var data = event.data;
      if (!data || typeof data !== 'string') return;
      var parts = data.split('|');
      if (parts.length < 2 || parts.length > 3) return;
      var height = parseInt(parts[1], 10);
      if (!height) return;

      document.querySelectorAll('.pmb-zoho__frame').forEach(function (frame) {
        if (frame.src.indexOf('formperma') === -1 || frame.src.indexOf(parts[0]) === -1) return;
        var wrap = frame.closest('.pmb-zoho');
        var next = height + 15 + 'px';
        if (wrap) {
          wrap.classList.add('is-loaded', 'is-autosized');
          wrap.style.setProperty('--pmb-zoho-h', next);
          wrap.style.setProperty('--pmb-zoho-h-mobile', next);
        }
        if (parts.length === 3) {
          /* step change / validation error — bring the form back into view */
          window.setTimeout(function () { frame.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); }, 300);
        }
      });
    }, false);
  }

  /* ---- Init ----------------------------------------------------------- */

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('form').forEach(function (form) {
      if (form.dataset.pmbZohoInit) return;
      if (form.getAttribute('data-pmb-zoho') === 'contact') {
        form.dataset.pmbZohoInit = '1';
        initDirect(form);
        return;
      }
      if (!endpoint) return;
      if (!isSubscribeForm(form)) return;
      form.dataset.pmbZohoInit = '1';
      initSubscribe(form);
    });
  }

  initEmbedResize();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
